import React, { useMemo, useState } from "react";
import { Sparkles, FileText, Loader2 } from "lucide-react";
import { FOLLOW_UP_OPTIONS, OUTCOME_LABELS } from "./lcrConstants";

// Small form building blocks for the Legal Consultation Record author.

export function StateBadge({ state, version }) {
  const cls =
    state === "published"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : (version || 0) > 0
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-slate-50 text-slate-500 border-slate-200";
  const label =
    state === "published"
      ? `Published · v${version || 1}`
      : (version || 0) > 0 ? "Draft in progress" : "Awaiting LCR";
  return (
    <span className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${cls}`} data-testid="lcr-state-badge">
      {label}
    </span>
  );
}

export function Row({ label, hint, warn, children }) {
  return (
    <div className={`rounded-2xl border p-4 sm:p-5 bg-white ${warn ? "border-amber-200 bg-amber-50/40" : "border-slate-200"}`} data-testid={`row-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <label className="block text-xs font-extrabold text-[#0A2540] mb-1 tracking-tight">{label}</label>
      {hint && <p className="text-[11px] text-slate-500 mb-3">{hint}</p>}
      {children}
    </div>
  );
}

export function ImproveButton({ onClick, loading, block }) {
  const cls = "inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border border-slate-200 bg-white hover:border-[#0A2540] hover:bg-[#FBF4DF] text-slate-600";
  if (block) {
    return (
      <div className="mt-2 flex justify-end">
        <button onClick={onClick} disabled={loading} className={cls} data-testid="ai-improve-btn">
          {loading ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
          {loading ? "Improving…" : "Improve"}
        </button>
      </div>
    );
  }
  return (
    <button
      onClick={onClick} disabled={loading}
      className={cls + " absolute right-2 top-1/2 -translate-y-1/2"}
      data-testid="ai-improve-btn"
    >
      {loading ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
      {loading ? "…" : "Improve"}
    </button>
  );
}

export function RichArea({ value, onChange, placeholder, testid, rows = 4 }) {
  return (
    <textarea
      data-testid={testid}
      rows={rows}
      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm leading-relaxed focus:outline-none focus:border-[#0A2540]"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

export function BulletList({ value, onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(value || []), v]);
    setDraft("");
  };
  return (
    <div data-testid={testid}>
      {(value || []).length > 0 && (
        <ul className="space-y-1 mb-2">
          {(value || []).map((item, i) => (
            <li key={`${item}-${i}`} className="flex items-center gap-2 text-sm bg-slate-50 rounded-xl px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0A2540]" />
              <input
                className="flex-1 bg-transparent focus:outline-none"
                value={item}
                onChange={(e) => {
                  const copy = [...value]; copy[i] = e.target.value; onChange(copy);
                }}
              />
              <button className="text-[11px] text-rose-500 hover:underline" onClick={() => onChange(value.filter((_, x) => x !== i))}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <input
          className="flex-1 h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-[#0A2540]"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder || "Add a bullet…"}
        />
        <button className="px-3 h-10 text-xs font-bold rounded-xl border border-slate-200 hover:border-[#0A2540]" onClick={add}>Add</button>
      </div>
    </div>
  );
}

export function DocList({ value, onChange, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    onChange([...(value || []), { title: t, doc_id: "" }]);
    setDraft("");
  };
  return (
    <div data-testid={testid}>
      {(value || []).length > 0 && (
        <ul className="space-y-1 mb-2">
          {(value || []).map((d, i) => (
            <li key={`${d}-${i}`} className="flex items-center gap-2 text-sm bg-slate-50 rounded-xl px-3 py-1.5">
              <FileText size={14} className="text-slate-500"/>
              <span className="flex-1 truncate">{d.title}</span>
              <button className="text-[11px] text-rose-500 hover:underline" onClick={() => onChange(value.filter((_, x) => x !== i))}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <input
          className="flex-1 h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-[#0A2540]"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="e.g. Sale Deed, FIR, Employment Contract"
        />
        <button className="px-3 h-10 text-xs font-bold rounded-xl border border-slate-200 hover:border-[#0A2540]" onClick={add}>Add</button>
      </div>
    </div>
  );
}

export function StepList({ value, onChange, presets, testid }) {
  const custom = useMemo(() => (value || []).filter((v) => !presets.includes(v)), [value, presets]);
  const [draft, setDraft] = useState("");
  const toggle = (p) => {
    if ((value || []).includes(p)) onChange(value.filter((x) => x !== p));
    else onChange([...(value || []), p]);
  };
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(value || []), v]);
    setDraft("");
  };
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-2">
        {presets.map((p) => (
          <button
            key={p} type="button" onClick={() => toggle(p)}
            className={`px-3 py-1 rounded-full text-[11px] font-bold border transition ${
              (value || []).includes(p)
                ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]"
                : "bg-white text-slate-600 border-slate-200 hover:border-[#0A2540]"
            }`}
          >
            {(value || []).includes(p) ? "✓ " : ""}{p}
          </button>
        ))}
      </div>
      {custom.length > 0 && (
        <ul className="space-y-1 mb-2">
          {custom.map((s, i) => (
            <li key={`${s}-${i}`} className="flex items-center gap-2 text-sm bg-slate-50 rounded-xl px-3 py-1.5">
              <span className="flex-1">{s}</span>
              <button className="text-[11px] text-rose-500 hover:underline" onClick={() => onChange(value.filter((x) => x !== s))}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <input
          className="flex-1 h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-[#0A2540]"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Add a custom step"
        />
        <button className="px-3 h-10 text-xs font-bold rounded-xl border border-slate-200 hover:border-[#0A2540]" onClick={add}>Add</button>
      </div>
    </div>
  );
}

export function FollowUpBlock({ value, onChange }) {
  return (
    <div className="space-y-3" data-testid="lcr-followup">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!value.recommended} onChange={(e) => onChange({ ...value, recommended: e.target.checked })}/>
        Yes, another consultation is recommended
      </label>
      {value.recommended && (
        <div className="pl-6 space-y-2">
          <div className="flex flex-wrap gap-2">
            {FOLLOW_UP_OPTIONS.map((o) => (
              <button
                key={o.key} type="button" onClick={() => onChange({ ...value, after: o.key })}
                className={`px-3 py-1 rounded-full text-[11px] font-bold border transition ${
                  value.after === o.key ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0A2540]"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          {value.after === "custom" && (
            <input type="date" className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
                   value={value.after_date || ""}
                   onChange={(e) => onChange({ ...value, after_date: e.target.value })} />
          )}
          <input
            className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm"
            placeholder="Optional explanation"
            value={value.note || ""}
            onChange={(e) => onChange({ ...value, note: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

export function OutcomePicker({ value, onChange }) {
  const toggle = (key) => {
    if ((value || []).includes(key)) onChange(value.filter((x) => x !== key));
    else onChange([...(value || []), key]);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="lcr-outcome">
      {Object.entries(OUTCOME_LABELS).map(([key, label]) => (
        <button
          key={key} type="button" onClick={() => toggle(key)}
          className={`px-3 py-1 rounded-full text-[11px] font-bold border transition ${
            (value || []).includes(key)
              ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]"
              : "bg-white text-slate-600 border-slate-200 hover:border-[#0A2540]"
          }`}
          data-testid={`outcome-${key}`}
        >
          {(value || []).includes(key) ? "✓ " : ""}{label}
        </button>
      ))}
    </div>
  );
}
